import React, { useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { useAppContext } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { getNormalizedMonthlyCost } from '../../utils/dateUtils';
import { cn } from '../../utils/cn';
import { convertCurrency, formatCurrency } from '../../utils/currencyUtils';

export const Sidebar = () => {
  const { state } = useAppContext();
  const { profile, logout } = useAuth();
  const navigate = useNavigate();

  const totalMonthlyConverted = useMemo(() => {
    return state.subscriptions.reduce((acc, sub) => {
      if (sub.status === 'cancelled') return acc;
      
      const monthlyCost = getNormalizedMonthlyCost(sub.recurringAmount, sub.recurrenceInterval);
      const convertedCost = convertCurrency(
        monthlyCost, 
        sub.currency || 'USD', 
        state.settings.preferredCurrency || 'USD', 
        state.exchangeRates
      );
      
      return acc + convertedCost;
    }, 0);
  }, [state.subscriptions, state.settings.preferredCurrency, state.exchangeRates]);

  const navItems = [
    { path: '/', label: 'Subscriptions', icon: 'List' },
    { path: '/calendar', label: 'Calendar', icon: 'Calendar' },
    { path: '/receipts', label: 'Receipts', icon: 'FileText' },
    { path: '/analytics', label: 'Analytics', icon: 'PieChart' },
    { path: '/settings', label: 'Settings', icon: 'Settings' },
    { path: '/account', label: 'Account', icon: 'User' },
  ];

  if (profile?.role === 'admin') {
    navItems.splice(navItems.length - 1, 0, { path: '/users', label: 'User Management', icon: 'Users' });
  }

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <aside className="w-72 bg-slate-900 text-slate-300 flex flex-col h-screen sticky top-0 hidden md:flex border-r border-slate-800">
      <div className="p-8 flex flex-col items-center text-center gap-4">
        <div className="w-20 h-20 flex-shrink-0 bg-white/5 p-3 rounded-[24px] border border-white/10 shadow-xl">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/981ecb6892631c31e92fca6d10d0332603596e5d429fd99cee63046a83be378b4365dffb7696bc6c52f453f1ea7f8977/images/aW1hZ2UvcG5n/10223f85c5cee27bf98990ec846a54be.png" 
            alt="SubSift Toucan Logo" 
            className="w-full h-full object-contain drop-shadow-2xl" 
          />
        </div>
        <div className="flex gap-1.5 items-baseline">
          <h1 className="font-black text-2xl tracking-tighter text-white">SubSift</h1>
          <h1 className="font-black text-2xl tracking-tighter text-teal-400">Toucan</h1>
        </div>
      </div>

      <nav className="flex-1 px-6 py-4 space-y-1.5 overflow-y-auto custom-scrollbar">
        {navItems.map((item) => (
          <NavLink 
            key={item.path} 
            to={item.path} 
            className={({ isActive }) => cn(
              "flex items-center gap-3.5 px-4 py-3.5 rounded-2xl transition-all duration-200 font-bold text-sm",
              isActive 
                ? "bg-teal-500 text-white shadow-lg shadow-teal-500/20" 
                : "text-slate-400 hover:bg-slate-800 hover:text-white"
            )}
          >
            <SafeIcon name={item.icon} className="w-5 h-5" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-6 border-t border-slate-800 space-y-4">
        <div className="bg-slate-800/50 rounded-2xl p-5 border border-white/5">
          <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Total ({state.settings.preferredCurrency})</p>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-white tracking-tight">
              {formatCurrency(totalMonthlyConverted, state.settings.preferredCurrency)}
            </span>
          </div>
        </div>
        <button 
          onClick={handleLogout} 
          className="w-full flex items-center gap-3 px-4 py-4 rounded-2xl text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all font-bold text-sm"
        >
          <SafeIcon name="LogOut" className="w-5 h-5" />
          Logout
        </button>
      </div>
    </aside>
  );
};